import { createContext, useContext } from 'react';
import { TimeRange } from "../types/TimeRange";
import * as dayjs from 'dayjs';
import * as QueryString from "query-string";
import 'dayjs/plugin/utc';
import { PERIOD_CUSTOM, PERIOD_ONE_HOUR, periodToValidIntervals } from "../component/Constants";

// Extend dayjs with the UTC plugin
dayjs.extend(require('dayjs/plugin/utc'));

// Check for QueryString parameters.
let queryString = QueryString.parse(window.location.search);

// Set the internal context object.
const savedTimeZone = window.localStorage.getItem('dbmar-timeZone')
const timeZoneValue = savedTimeZone ? JSON.parse(savedTimeZone) : Intl.DateTimeFormat().resolvedOptions().timeZone
const isUTC = timeZoneValue === 'UTC'
const timeNow = isUTC ? dayjs.utc() : dayjs.default()
const savedInterval = JSON.parse(window.localStorage.getItem('dbmar-interval') || '0')
const validInterval = periodToValidIntervals.hours_1.includes(savedInterval)

let timeRange: TimeRange = {
    interval: validInterval ? savedInterval : 0,
    timezone: timeZoneValue || Intl.DateTimeFormat().resolvedOptions().timeZone,
    previousFrom: timeNow.subtract(2, 'hour').second(0),
    from: timeNow.subtract(1, 'hour').second(0),
    to: timeNow.second(0),
    dateFormat: 'YYYY-MM-DD+HH:mm:00',
    period: PERIOD_ONE_HOUR,
    autorefresh: false
};

if (queryString.fm && queryString.to && queryString.tz) {
    const timezone = (typeof queryString.tz === 'object' ? queryString.tz[0] : queryString.tz) || 'UTC'
    const intervalParam = queryString.interval || '0'
    if (queryString.tz !== timeZoneValue) {
        // Convert the times from the initial timezone to the desired timezone
        const fromTime = dayjs.tz(queryString.fm.toString(), 'YYYY-MM-DD+HH:mm:ss', timezone).tz(timeZoneValue);
        const toTime = dayjs.tz(queryString.to.toString(), 'YYYY-MM-DD+HH:mm:ss', timezone).tz(timeZoneValue);

        const fm = fromTime.format('YYYY-MM-DD+HH:mm:ss');
        const to = toTime.format('YYYY-MM-DD+HH:mm:ss');
        
        // Preserve all other URL parameters except fm, to, tz, and interval
        const preservedParams = { ...queryString };
        delete preservedParams.fm;
        delete preservedParams.to;
        delete preservedParams.tz;
        delete preservedParams.interval;
        
        const additionalParams = QueryString.stringify(preservedParams);
        const separator = additionalParams ? '&' : '';
        window.location.href = `${window.location.origin}${window.location.pathname}?fm=${fm}&to=${to}&tz=${timeZoneValue}&interval=${intervalParam}${separator}${additionalParams}`;
    } else {
        // Override the default settings.
        timeRange.from = dayjs.default(queryString.fm.toString(), 'YYYY-MM-DD+HH:mm', timezone)
        timeRange.from = dayjs.default(timeRange.from)
        timeRange.to = dayjs.default(queryString.to.toString(), 'YYYY-MM-DD+HH:mm', timezone)
        timeRange.to = dayjs.default(timeRange.to)
        timeRange.previousFrom = timeRange.from.subtract(timeRange.to.diff(timeRange.from, 'millisecond'), 'millisecond')
        timeRange.previousFrom = dayjs.default(timeRange.previousFrom)
        timeRange.period = PERIOD_CUSTOM
        if (queryString.interval) {
            const intervalURLvalue = parseInt(intervalParam.toString())
            timeRange.interval = intervalURLvalue
            window.localStorage.setItem('dbmar-interval', JSON.stringify(intervalURLvalue))
        }
        if (queryString.tz) {
            const timeZoneURLvalue = queryString.tz || timeZoneValue
            timeRange.timezone = timeZoneURLvalue
            window.localStorage.setItem('dbmar-timeZone', JSON.stringify(timeZoneURLvalue))
        }
    }
}

export type SetTimeRangeParamType = Pick<TimeRange, 'timezone' | 'from' | 'to' | 'interval' | 'period' | 'autorefresh'>

// Define the context type, including the update function.
export type TimeRangeContextType = {
    timeRange: TimeRange;
    setTimeRange: (timeRange: SetTimeRangeParamType) => void;
    getTimeRangeQueryString: (previous?: boolean, interval?: number) => string,
    getTimeRangeRedirectString: () => string
}

// Define the context object.
export const TimeRangeContext = createContext<TimeRangeContextType>({
    timeRange: timeRange,
    getTimeRangeQueryString: (previous = false) => {
        let from, to;
        from = previous
                ? timeRange.previousFrom.format('YYYY-MM-DD+HH:mm:ss')
                : timeRange.from.format('YYYY-MM-DD+HH:mm:ss');

        to = previous
                ? timeRange.from.format('YYYY-MM-DD+HH:mm:ss')
                : timeRange.to.format('YYYY-MM-DD+HH:mm:ss');

        return `from=${from}&to=${to}&tz=${timeRange.timezone}&interval=${timeRange.interval || 0}`;
    },

    getTimeRangeRedirectString: () => {
        const fm = timeRange.from.format('YYYY-MM-DD+HH:mm:ss');
        const to = timeRange.to.format('YYYY-MM-DD+HH:mm:ss');
        return `fm=${fm}&to=${to}&tz=${timeRange.timezone}&interval=${timeRange.interval}`;
    },
    setTimeRange: (newTimeRange: SetTimeRangeParamType) => {
        const from = newTimeRange.from
        const to = newTimeRange.to
        timeRange.previousFrom = from.subtract(newTimeRange.to.diff(from, 'millisecond'), 'millisecond');
        timeRange.from = from;
        timeRange.to = to;
        timeRange.timezone = newTimeRange.timezone;
        timeRange.interval = newTimeRange.interval;
        timeRange.period = newTimeRange.period;
        timeRange.autorefresh = newTimeRange.autorefresh;
    }
});
TimeRangeContext.displayName = 'TimeRange';

// Export the update function for the context object.
export const useTimeRange = () => useContext(TimeRangeContext);
